/**
 * Company logo resolution — single source of truth for "what image represents
 * this company". Resolution order, most authoritative first:
 *   1. An explicit logo (e.g. uploaded/imported during onboarding → crm_accounts.logo_url)
 *   2. The favicon of the company website domain
 *   3. The favicon of the company email domain (excluding free/consumer providers)
 *   4. null → callers fall back to initials / a colored marker.
 *
 * Favicons come from Google's S2 service, which returns a generic globe for
 * unknown domains (it never 404s), so an <img onError> handler should still keep
 * a colored fallback for the free-mail case where we return null upfront.
 */

import { isFreeEmailDomain } from '../../supabase/functions/_shared/email-domains';

/** Normalize a raw host/URL to a bare registrable domain, or null if unusable. */
function cleanDomain(raw?: string | null): string | null {
  if (!raw) return null;
  const d = raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .split('?')[0]
    .split('#')[0]
    .trim();
  return d.includes('.') ? d : null;
}

export function domainFromWebsite(website?: string | null): string | null {
  return cleanDomain(website);
}

/** Email domain, excluding free/consumer providers (those aren't a company brand). */
export function domainFromEmail(email?: string | null): string | null {
  if (!email || !email.includes('@')) return null;
  const domain = cleanDomain(email.split('@').pop());
  if (!domain || isFreeEmailDomain(domain)) return null;
  return domain;
}

/** Google S2 favicon URL for a domain. Falls back to a globe glyph server-side. */
export function faviconUrl(domain: string, size = 64): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

/**
 * Normalise une URL de logo pour l'affichage. Les anciens `logo_url` pointent
 * vers le service `gstatic.com/faviconV2`, qui **404** pour les domaines sans
 * favicon (d'ou un flot d'erreurs console + un blocage CSP en prod). On les
 * reecrit vers le service S2, qui sert un globe generique au lieu de 404 et
 * tient sur un seul host autorise. Les vraies URLs (logo uploade) passent
 * telles quelles.
 */
export function normalizeLogoUrl(url?: string | null): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    const isGstaticFavicon =
      parsed.hostname.endsWith('gstatic.com') && parsed.pathname.includes('faviconV2');
    if (isGstaticFavicon) {
      const domain = domainFromWebsite(parsed.searchParams.get('url'));
      return domain ? faviconUrl(domain) : null;
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

export interface LogoSources {
  /** Explicit logo URL (crm_accounts.logo_url), highest priority. */
  logoUrl?: string | null;
  /** Company website (crm_accounts.website). */
  website?: string | null;
  /** A contact email — its domain is used as a last resort. */
  email?: string | null;
}

/**
 * Best image URL representing a company, or null when nothing usable exists.
 * See module docs for the resolution order.
 */
export function resolveCompanyLogo({ logoUrl, website, email }: LogoSources): string | null {
  if (logoUrl && logoUrl.trim()) return logoUrl.trim();
  const domain = domainFromWebsite(website) ?? domainFromEmail(email);
  return domain ? faviconUrl(domain) : null;
}
